import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';

import { auth } from '../config/firebase';
import { getAdminAuthToken, getApiBaseUrl } from '../services/auth_service';

// ─── Constants ────────────────────────────────────────────────────────────────

const DAY_MIN_HOURS = 7;
const SECONDS_IN_HOUR = 3600;
const ISO_DATE_LENGTH = 10;
const MIN_SECONDS = DAY_MIN_HOURS * SECONDS_IN_HOUR;
const MORNING_END_HOUR = 12;
const AFTERNOON_END_HOUR = 18;

// ─── Route → page title map ───────────────────────────────────────────────────

const PAGE_TITLES = {
	'/todos': 'My Todos',
	'/profile': 'My Profile',
	'/wiki': 'Knowledge Base',
	'/time-tracking': 'Time Tracking',
	'/tracking-app': 'Events',
	'/code-library': 'Library',
	'/looker': 'Looker',
	'/setup-totp': 'Setup 2FA',
	'/admin/dashboard': 'Admin Dashboard',
	'/admin/users': 'Users',
	'/admin/permissions': 'Permissions',
	'/admin/logs': 'Logs',
	'/admin/looker': 'Looker Settings',
	'/admin/integrations': 'Integrations',
};

const getPageTitle = pathname => {
	if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
	if (pathname.startsWith('/wiki/')) return 'Knowledge Base';
	if (pathname.startsWith('/admin')) return 'Admin';
	return null;
};

// ─── WelcomeSection ───────────────────────────────────────────────────────────

export function WelcomeSection({ titleOverride = null }) {
	const navigate = useNavigate();
	const location = useLocation();
	const [userName, setUserName] = useState('');

	const isDashboard = location.pathname === '/dashboard';
	const pageTitle = getPageTitle(location.pathname);

	// ── Clockify state ──────────────────────────────────────────────────────────
	const [dashboardDailyTotal, setDashboardDailyTotal] = useState(null);
	const [isLoadingDashboardTracking, setIsLoadingDashboardTracking] = useState(true);
	const [clockifyKeyMissing, setClockifyKeyMissing] = useState(false);

	// ── Date / greeting ─────────────────────────────────────────────────────────
	const currentDate = new Date();
	const hours = currentDate.getHours();
	const formattedDateFull = currentDate.toLocaleDateString('en-GB', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
	const formattedDateShort = currentDate.toLocaleDateString('en-GB', {
		weekday: 'short',
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});

	let greeting;
	if (hours < MORNING_END_HOUR) greeting = 'Good morning';
	else if (hours < AFTERNOON_END_HOUR) greeting = 'Good afternoon';
	else greeting = 'Good evening';

	// ── Fetch user ──────────────────────────────────────────────────────────────
	useEffect(() => {
		const user = auth.currentUser;
		if (user) setUserName(user.displayName || user.email);
	}, []);

	// ── Fetch Clockify daily total ──────────────────────────────────────────────
	useEffect(() => {
		const fetchDashboardTracking = async () => {
			setIsLoadingDashboardTracking(true);
			try {
				const token = await getAdminAuthToken();
				const authHeaders = { Authorization: `Bearer ${token}` };
				const apiBaseUrl = getApiBaseUrl();

				const keyStatusResponse = await fetch(`${apiBaseUrl}/api/clockify/key-status`, {
					headers: authHeaders,
				});
				if (keyStatusResponse.ok) {
					const keyStatus = await keyStatusResponse.json();
					if (keyStatus?.hasKey === false) {
						setClockifyKeyMissing(true);
						setDashboardDailyTotal(null);
						return;
					}
				}

				setClockifyKeyMissing(false);
				const workspaceResponse = await fetch(`${apiBaseUrl}/api/clockify/workspaces`, {
					headers: authHeaders,
				});
				if (!workspaceResponse.ok) {
					setDashboardDailyTotal(null);
					return;
				}

				const workspaces = await workspaceResponse.json();
				const selectedWorkspaceId = Array.isArray(workspaces) && workspaces[0]?.id;
				if (!selectedWorkspaceId) {
					setDashboardDailyTotal(null);
					return;
				}

				const date = new Date().toISOString().slice(0, ISO_DATE_LENGTH);
				const summaryResponse = await fetch(
					`${apiBaseUrl}/api/clockify/summary/daily?workspaceId=${encodeURIComponent(selectedWorkspaceId)}&date=${encodeURIComponent(date)}`,
					{ headers: authHeaders }
				);
				if (!summaryResponse.ok) {
					setDashboardDailyTotal(null);
					return;
				}

				const summary = await summaryResponse.json();
				setDashboardDailyTotal(summary || null);
			} catch {
				setDashboardDailyTotal(null);
			} finally {
				setIsLoadingDashboardTracking(false);
			}
		};

		fetchDashboardTracking();
	}, []);

	// ── Derived tracking values ─────────────────────────────────────────────────
	const trackedSeconds = dashboardDailyTotal?.totalSeconds || 0;
	const pct = Math.min(100, Math.round((trackedSeconds / MIN_SECONDS) * 100));

	let trackedTimeDisplay = '—';
	if (clockifyKeyMissing) trackedTimeDisplay = 'Setup required';
	else if (dashboardDailyTotal) trackedTimeDisplay = dashboardDailyTotal.human;
	else if (isLoadingDashboardTracking) trackedTimeDisplay = '…';

	const goToProfile = () => navigate('/profile');

	return (
		<section
			aria-label='User Info'
			className='sticky top-0 z-40 px-6 py-4 pl-[45px] md:pl-6
			           flex items-center justify-between overflow-hidden
			           border-b border-[rgba(255,255,255,0.07)] min-h-[96px]'
			style={{ background: '#020820' }}
		>
			{/* Watermark */}
			<img
				src='/conversio_2.png'
				alt=''
				aria-hidden='true'
				className='absolute pointer-events-none select-none'
				style={{ left: '35%', top: '-10px', width: '460px', opacity: 0.06, zIndex: 0 }}
			/>

			{/* ── Left: greeting (dashboard) or page title (everywhere else) ── */}
			<div className='relative z-10 flex flex-col items-start overflow-hidden'>
				{isDashboard ? (
					<>
						<p className='text-[13px] leading-none mb-1 text-gray-400'>
							<span className='hidden md:inline'>{formattedDateFull}</span>
							<span className='md:hidden'>{formattedDateShort}</span>
						</p>
						<p className='text-[16px] font-semibold text-white leading-tight truncate'>
							{greeting}
							{userName && (
								<span className='hidden sm:inline' aria-label='User Email'>
									, <span style={{ color: '#018DBB' }}>{userName}</span>
								</span>
							)}
						</p>
					</>
				) : (
					<p className='text-[18px] font-bold text-white leading-tight truncate'>
						{titleOverride ?? pageTitle ?? 'App'}
					</p>
				)}
			</div>

			{/* ── Centre: time-tracked bar (always visible on md+) ── */}
			<div className='relative z-10 hidden md:flex flex-col items-end flex-1 mx-8'>
				<div className='flex items-center gap-3 mb-[3px]'>
					<span className='text-[10px] font-semibold uppercase tracking-wider text-gray-400'>
						Tracked today
					</span>
					<span className='text-[11px] text-gray-400'>
						{pct}% of {DAY_MIN_HOURS}h
					</span>
				</div>
				<p className='text-[22px] font-bold text-white leading-tight mb-[6px]'>
					{trackedTimeDisplay}
				</p>
				<div
					className='w-[200px] h-[6px] rounded-full overflow-hidden'
					style={{ background: 'rgba(255,255,255,0.1)' }}
				>
					<div
						className='h-full rounded-full transition-all duration-500'
						style={{
							width: `${pct}%`,
							background: 'linear-gradient(90deg, #FF0B78, #E300C9)',
						}}
					/>
				</div>
			</div>

			{/* ── Right: hamburger + avatar — desktop ── */}
			<button
				type='button'
				onClick={goToProfile}
				aria-label='Open profile'
				className='relative z-10 flex items-center gap-2 h-[35px] sm:h-[40px] px-4
				           rounded-full border md:flex hidden outline-none focus:outline-none'
				style={{
					background: 'rgba(255,255,255,0.1)',
					borderColor: 'rgba(255,255,255,0.15)',
				}}
			>
				<span className='flex flex-col justify-center gap-[3px] h-5 w-6' aria-hidden='true'>
					<span
						className='block h-[2px] w-5 rounded'
						style={{ backgroundColor: 'white' }}
					/>
					<span
						className='block h-[2px] w-5 rounded'
						style={{ backgroundColor: 'white' }}
					/>
					<span
						className='block h-[2px] w-5 rounded'
						style={{ backgroundColor: 'white' }}
					/>
				</span>
				<div
					className='h-[28px] w-[28px] sm:h-[32px] sm:w-[32px] rounded-full bg-gradient-to-br from-indigo-400 to-purple-600 flex items-center justify-center text-white font-semibold text-xs'
					aria-label='User avatar'
				>
					{userName.charAt(0).toUpperCase()}
				</div>
			</button>

			{/* ── Right: avatar only — mobile ── */}
			<button
				type='button'
				onClick={goToProfile}
				aria-label='Open profile'
				className='relative z-10 md:hidden bg-transparent border-none outline-none focus:outline-none'
			>
				<div
					className='h-[28px] w-[28px] sm:h-[32px] sm:w-[32px] rounded-full bg-gradient-to-br from-indigo-400 to-purple-600 flex items-center justify-center text-white font-semibold text-xs'
					aria-label='User avatar'
				>
					{userName.charAt(0).toUpperCase()}
				</div>
			</button>
		</section>
	);
}

WelcomeSection.propTypes = {
	titleOverride: PropTypes.string,
};
